// The console's theme (ADR-0041): dark, light, or whatever this device prefers — a member's choice on this
// device, kept in the browser's storage beside the console's other per-device conveniences. Dark when
// nothing is stored, so every member sees the console they saw before the light theme existed until they
// choose otherwise (a choice that changed what a venue sees the day it shipped would be a bug, CLAUDE.md).
//
// The kiosk and the player surfaces are dark whatever this says: they pin `data-theme="dark"` themselves.
//
// `paintConsoleTheme` writes the resolved theme onto `<html>` too, so the page ground, the scrollbars and the
// form controls the browser draws follow it; `main.tsx` calls it before the first render, so a light-theme
// member does not see the dark ground first.

import { useEffect, useSyncExternalStore } from 'react';

export const CONSOLE_THEMES = ['dark', 'light', 'system'] as const;
export type ConsoleTheme = (typeof CONSOLE_THEMES)[number];

/** The key in the browser's storage. */
export const CONSOLE_THEME_KEY = 'clavian.console.theme';

const LIGHT_QUERY = '(prefers-color-scheme: light)';

function isConsoleTheme(value: unknown): value is ConsoleTheme {
  return typeof value === 'string' && (CONSOLE_THEMES as readonly string[]).includes(value);
}

/** The member's choice on this device; dark when there is none or the storage cannot be read. */
export function readConsoleTheme(): ConsoleTheme {
  try {
    const stored = window.localStorage.getItem(CONSOLE_THEME_KEY);
    return isConsoleTheme(stored) ? stored : 'dark';
  } catch {
    return 'dark';
  }
}

/** `system` made concrete: light only when the device asks for it. */
export function resolveConsoleTheme(theme: ConsoleTheme): 'dark' | 'light' {
  if (theme !== 'system') return theme;
  return typeof window.matchMedia === 'function' && window.matchMedia(LIGHT_QUERY).matches ? 'light' : 'dark';
}

/**
 * The resolved theme on `<html>`: its ground and the browser's own controls follow it, and the browser's
 * `theme-color` (a phone's address bar, a PWA's title bar) takes the page ground's token, so it matches the page.
 */
export function paintConsoleTheme(theme: ConsoleTheme): void {
  const mode = resolveConsoleTheme(theme);
  const root = document.documentElement;
  root.dataset['theme'] = mode;
  root.style.colorScheme = mode;
  const ground = getComputedStyle(root).getPropertyValue('--clavian-bg').trim();
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta !== null && ground !== '') meta.content = ground;
}

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab of the console changed it.
  const onStorage = (event: StorageEvent) => {
    if (event.key === CONSOLE_THEME_KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** Store a choice and tell every part of the console that reads it. */
export function setConsoleTheme(theme: ConsoleTheme): void {
  try {
    window.localStorage.setItem(CONSOLE_THEME_KEY, theme);
  } catch {
    // A private window without storage: the choice lasts until the page is closed.
  }
  paintConsoleTheme(theme);
  for (const listener of listeners) listener();
}

/**
 * The member's choice, and the theme it resolves to now. While the choice is `system`, a change of the
 * device's own preference repaints the console at once.
 */
export function useConsoleTheme(): { readonly theme: ConsoleTheme; readonly resolved: 'dark' | 'light' } {
  const theme = useSyncExternalStore(subscribe, readConsoleTheme, () => 'dark' as const);
  useEffect(() => {
    paintConsoleTheme(theme);
    if (theme !== 'system' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(LIGHT_QUERY);
    const repaint = () => {
      paintConsoleTheme('system');
      for (const listener of listeners) listener();
    };
    query.addEventListener('change', repaint);
    return () => query.removeEventListener('change', repaint);
  }, [theme]);
  return { theme, resolved: resolveConsoleTheme(theme) };
}
